import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Printer } from "lucide-react";
import Logo from "./Logo";
import { contentAPI } from "../services/api";

const Footer = () => {
  const [logoData, setLogoData] = useState({ logo_url: null, logo_red_url: null });

  useEffect(() => {
    // Fetch logo data from API (same as Header)
    const fetchLogoData = async () => {
      try {
        const sections = await contentAPI.getSections("settings").catch(() => []);
        const logoSection = sections.find(s => s.section_type === "logo");
        if (logoSection?.content_data) {
          setLogoData({
            logo_url: logoSection.content_data.logo_url || null,
            logo_red_url: logoSection.content_data.logo_red_url || null,
          });
        }
      } catch (error) {
        console.error("Error fetching logo data:", error);
      }
    };
    fetchLogoData();
  }, []);

  return (
    <footer className="bg-[var(--vortex-surface)] pt-10 sm:pt-16 pb-6 sm:pb-8 border-t-2 border-[var(--vortex-border)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full min-w-0">
        <div className="grid md:grid-cols-3 gap-8 sm:gap-12 mb-8 sm:mb-12">
          {/* Brand Section */}
          <div className="space-y-6">
            <Logo 
              size="small" 
              logoImage={logoData.logo_url}
              logoImageRed={logoData.logo_red_url}
            />
            <p className="text-lg text-[var(--vortex-text-secondary)] leading-relaxed">
              Professionelle häusliche Pflege und Betreuung mit Kompetenz, Herz und Respekt.
            </p>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-[var(--vortex-text-primary)] mb-4">
              Kontakt
            </h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-[var(--vortex-text-secondary)] mt-1 flex-shrink-0" />
                <div>
                  <a
                    href="tel:+4921332184111"
                    className="text-lg text-[var(--vortex-text-primary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
                  >
                    021332184111
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Printer className="w-5 h-5 text-[var(--vortex-text-secondary)] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-lg text-[var(--vortex-text-primary)]">
                    021332184110
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-[var(--vortex-text-secondary)] mt-1 flex-shrink-0" />
                <a
                  href="mailto:info@serafina-pflegedienst.de"
                  className="text-lg text-[var(--vortex-text-primary)] hover:text-[var(--vortex-accent-primary)] transition-colors break-all"
                >
                  info@serafina-pflegedienst.de
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[var(--vortex-text-secondary)] mt-1 flex-shrink-0" />
                <div>
                  <a
                    href="https://www.google.com/maps/place/Hindenburgstra%C3%9Fe+77,+41542+Dormagen/@51.1124331,6.7591731,634m/data=!3m2!1e3!4b1!4m6!3m5!1s0x47bf350862e56743:0xef6851e8716adbc6!8m2!3d51.1124298!4d6.761748!16s%2Fg%2F11c1b0sjjt?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoKLDEwMDc5MjA2OUgBUAM%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors leading-relaxed"
                  >
                    Hindenburgstr. 77<br />
                    41542 Dormagen
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-[var(--vortex-text-secondary)] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-lg font-semibold text-[var(--vortex-text-primary)]">Bürozeiten</p>
                  <p className="text-base text-[var(--vortex-text-secondary)] leading-relaxed whitespace-pre-line">
                    Montag bis Freitag: 09:00-15:00 Uhr
                    und nach telefonischer Vereinbarung
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-[var(--vortex-text-primary)] mb-4">
              Schnellzugriff
            </h3>
            <nav className="flex flex-col space-y-2">
              <a
                href="#hero"
                className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
              >
                Startseite
              </a>
              <a
                href="#services"
                className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
              >
                Leistungen
              </a>
              <a
                href="#about"
                className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
              >
                Über uns
              </a>
              <a
                href="#qualitaet"
                className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
              >
                Qualitätssicherung
              </a>
              <a
                href="#kontakt"
                className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
              >
                Kontakt
              </a>
              <Link
                to="/impressum"
                className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
              >
                Impressum
              </Link>
              <Link
                to="/datenschutz"
                className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
              >
                Datenschutz
              </Link>
              <Link
                to="/cookies"
                className="text-lg text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors"
              >
                Cookies
              </Link>
            </nav>
          </div>
        </div>

        {/* Analytics disclosure (GA & Clarity) – recommended for transparency */}
        <p className="text-sm text-[var(--vortex-text-secondary)] text-center max-w-2xl mx-auto mb-4">
          Wir nutzen Google Analytics und Microsoft Clarity, um die Nutzung
          unserer Website auszuwerten und zu verbessern. Durch die Nutzung der
          Website stimmen Sie der Erhebung und Nutzung dieser Daten durch uns
          und die genannten Anbieter zu. Details finden Sie in unserer{" "}
          <Link
            to="/datenschutz"
            className="text-[var(--vortex-accent-primary)] hover:underline"
          >
            Datenschutzerklärung
          </Link>
          .
        </p>

        {/* Bottom Bar - single row, same baseline */}
        <div className="pt-6 sm:pt-8 border-t border-[var(--vortex-border)]">
          <div className="flex flex-wrap items-baseline justify-center sm:justify-between gap-x-2 gap-y-1 text-sm text-[var(--vortex-text-secondary)] text-center sm:text-left">
            <span>© {new Date().getFullYear()} Serafina Pflegedienst GmbH. Alle Rechte vorbehalten.</span>
            <span className="hidden sm:inline" aria-hidden="true"> · </span>
            <a
              href="https://vortexagency.co/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--vortex-text-secondary)] hover:text-[var(--vortex-accent-primary)] transition-colors inline"
            >
              Erstellt von Vortex Creative Agency
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
